import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getFoamColor, MIN_DESKTOP_WIDTH, type BlockPresetId } from "@/config/designer";
import { DesignerEngine } from "@/core/engine/DesignerEngine";
import { clearDesign, loadDesign } from "@/core/persistence/storage";
import { createHoldMaterial } from "@/scene/holdMaterial";
import { useDesignerStore } from "@/state/designerStore";
import { detectWebgl, ErrorBoundary, Loader, MobileNotice, WebglNotice } from "@/ui/Fallbacks";
import { Notices } from "@/ui/Overlays";
import { SetupScreen } from "@/ui/SetupScreen";
import { Workspace } from "@/ui/Workspace";

export function App(): React.ReactElement {
  const phase = useDesignerStore((s) => s.phase);
  const setPhase = useDesignerStore((s) => s.setPhase);
  const engine = useDesignerStore((s) => s.engine);
  const setEngine = useDesignerStore((s) => s.setEngine);
  const notify = useDesignerStore((s) => s.notify);
  const foamColor = useDesignerStore((s) => s.foamColor);

  const [webgl] = useState(detectWebgl);
  const [narrow, setNarrow] = useState(() => window.innerWidth < MIN_DESKTOP_WIDTH);
  const engineRef = useRef<DesignerEngine | null>(null);

  const hold = useMemo(() => createHoldMaterial(), []);

  useEffect(() => {
    hold.material.color.set(getFoamColor(foamColor).hex);
  }, [foamColor, hold.material]);

  useEffect(() => {
    const onResize = (): void => setNarrow(window.innerWidth < MIN_DESKTOP_WIDTH);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The foam material lives for the whole session and is deliberately NOT
  // disposed here: React StrictMode remounts effects in development, which
  // would tear down the material the scene is still using. Only the engine —
  // which holds the geometry buffers — needs releasing.
  useEffect(() => () => engineRef.current?.dispose(), []);

  const swapEngine = useCallback(
    (next: DesignerEngine | null) => {
      engineRef.current?.dispose();
      engineRef.current = next;
      setEngine(next);
      if (import.meta.env.DEV) {
        // Development-only handle for poking at geometry from the console.
        (window as unknown as { ethosEngine?: DesignerEngine | null }).ethosEngine = next;
      }
    },
    [setEngine],
  );

  const start = useCallback(
    (presetId: BlockPresetId) => {
      setPhase("preparing");
      // Yield a frame so the loader paints before the field is allocated.
      window.setTimeout(() => {
        try {
          swapEngine(new DesignerEngine(presetId, hold.material));
          setPhase("workspace");
        } catch (error) {
          console.error(error);
          notify("Could not prepare that block. Try a smaller one.", "warn");
          setPhase("setup");
        }
      }, 30);
    },
    [hold, notify, setPhase, swapEngine],
  );

  const resume = useCallback(() => {
    setPhase("preparing");
    void (async () => {
      const saved = await loadDesign();
      if (!saved) {
        notify("No saved design was found.", "warn");
        setPhase("setup");
        return;
      }
      try {
        const next = new DesignerEngine(saved.presetId, hold.material);
        if (!next.restore(saved)) {
          next.dispose();
          notify("The saved design no longer matches its block and was skipped.", "warn");
          setPhase("setup");
          return;
        }
        swapEngine(next);
        setPhase("workspace");
      } catch (error) {
        console.error(error);
        notify("The saved design could not be opened.", "warn");
        setPhase("setup");
      }
    })();
  }, [hold, notify, setPhase, swapEngine]);

  const onContextLost = useCallback(() => {
    notify("The graphics context was lost. Reload the page to continue.", "warn");
  }, [notify]);

  const discardAndReturnToSetup = useCallback(() => {
    void clearDesign().finally(() => {
      swapEngine(null);
      setPhase("setup");
      useDesignerStore.getState().setConfirmingNew(false);
    });
  }, [setPhase, swapEngine]);

  const newDesign = useCallback(() => {
    // Confirm only when there is work to lose, and confirm in the app's own
    // dialog rather than a browser alert.
    if (engineRef.current?.hasEdits) {
      useDesignerStore.getState().setConfirmingNew(true);
      return;
    }
    discardAndReturnToSetup();
  }, [discardAndReturnToSetup]);

  if (!webgl) return <WebglNotice />;
  if (narrow) return <MobileNotice />;

  return (
    <ErrorBoundary>
      <Notices />
      {phase === "workspace" && engine ? (
        <Workspace
          engine={engine}
          uniforms={hold.uniforms}
          onContextLost={onContextLost}
          onNewDesign={newDesign}
          onConfirmNewDesign={discardAndReturnToSetup}
        />
      ) : phase === "preparing" ? (
        <Loader label="Preparing your foam blank…" />
      ) : (
        <SetupScreen onStart={start} onContinue={resume} />
      )}
    </ErrorBoundary>
  );
}
